import _ from 'lodash';
import React from 'react';
import Dropzone from 'react-dropzone';
import moment from 'moment';
import postal from 'postal';
import update from 'immutability-helper';
import debounceAction from '../../../shared/debounce-action';
import Loading from '../../../shared/loading';
import defaultOnErrorHandler from '../../../shared/default-on-error-handler';
import Validator from '../../../shared/validator';
import mutate from '../../../shared/mutate';
import ErrorBoundary from '../../../shared/error-boundary';
import PasswordChangeModal from './password-change-modal';
import EmailChangeModal from './email-change-modal';
import { defaultProfileCompleteness, profileCompletenessType } from './headers-components/score';
import HeaderWrapper from './headers-components/wrapper';
import {
  setFirstName, setLastName, setOrganisation, setPosition, setDob,
  getIncompleteDetails, getDetails, removePicture, updatePicture,
} from '../../../services/profiles/personal-details-service';

export default class PersonalDetailsEditor extends React.Component {
  static propTypes = { profileCompleteness: profileCompletenessType, newNavEnabled: false };

  static defaultProps = { profileCompleteness: defaultProfileCompleteness, newNavEnabled: false };

  constructor(props, context) {
    super(props, context);

    this.validator = new Validator({
      lastNameRequired: state => !!state.profile.lastName,
    });
    this.state = {
      profile: {
        firstName: '',
        lastName: '',
        organisation: '',
        position: '',
        email: '',
        profilePictureUrl: '',
      },
      day: 0,
      month: 0,
      year: 0,
      investorTypes: [],
      incompleteDetails: [],
      validations: this.validator.getValidations(),
    };

    this.saveFirstName = debounceAction({
      getCurrentValue: () => this.state.profile.firstName,
      actionPromise: () => setFirstName(this.state.profile.firstName),
      onBeforeAction: () => this.firstNameLoading.start(),
      onError: e => defaultOnErrorHandler(e, 'Error saving first name'),
      onAfterAction: () => {
        this.firstNameLoading.stop();
        postal.publish({
          channel: 'events.user',
          topic: 'first-name-changed',
          data: {
            firstName: this.state.profile.firstName,
          },
        });
      },
    });
    this.saveLastName = debounceAction({
      getCurrentValue: () => this.state.profile.lastName,
      getIsValid: () => !!this.state.profile.lastName,
      actionPromise: () => setLastName(this.state.profile.lastName),
      onBeforeAction: () => this.lastNameLoading.start(),
      onError: e => defaultOnErrorHandler(e, 'Error saving last name'),
      onAfterAction: () => this.lastNameLoading.stop(),
    });
    this.saveOrganisation = debounceAction({
      getCurrentValue: () => this.state.profile.organisation,
      actionPromise: () => setOrganisation(this.state.profile.organisation),
      onBeforeAction: () => this.organisationLoading.start(),
      onError: e => defaultOnErrorHandler(e, 'Error saving company'),
      onAfterAction: () => {
        this.header && this.header.update({ organization: this.state.profile.organisation });
        this.organisationLoading.stop();
      },
    });
    this.savePosition = debounceAction({
      getCurrentValue: () => this.state.profile.position,
      actionPromise: () => setPosition(this.state.profile.position),
      onBeforeAction: () => this.positionLoading.start(),
      onError: e => defaultOnErrorHandler(e, 'Error saving job title'),
      onAfterAction: () => {
        this.header && this.header.update({ position: this.state.profile.position });
        this.positionLoading.stop();
      },
    });
    this.saveDateOfBirth = debounceAction({
      getCurrentValue: () => this.dateOfBirthTimeStamp,
      getIsValid: () => this.dateOfBirthTimeStamp.isValid(),
      actionPromise: () => setDob(this.dateOfBirthTimeStamp),
      onBeforeAction: () => this.dateOfBirthLoading.start(),
      onError: e => defaultOnErrorHandler(e, 'Error saving date of birth'),
      onAfterAction: () => {
        this.dateOfBirthLoading.stop();
        this.setState(state => update(state, {
          incompleteDetails: arr => arr.filter(x => x !== 'date_of_birth'),
        }));
        postal.publish({
          channel: 'profile.events',
          topic: 'personal-details-changed',
        });
      },
    });
  }

  componentDidMount() {
    this.loading.start();
    getIncompleteDetails()
      .then(r => this.setState({ incompleteDetails: _.get(r, 'data.incomplete_details', []) }))
      .catch(error => defaultOnErrorHandler(error, 'Error loading incomplete details'));

    getDetails()
      .then(result => {
        const profile = _.get(result, 'data.profile', {});
        this.loading.stop();
        this.setState({
          profile: {
            firstName: profile.first_name,
            lastName: profile.last_name,
            organisation: profile.organisation || '',
            position: profile.position || '',
            email: profile.email,
            profilePictureUrl: profile.profile_picture_url,
          },
          investorTypes: _.get(result, 'data.investor_types', []),
          day: parseInt(moment(profile.date_of_birth).format('DD'), 10) || 0,
          month: parseInt(moment(profile.date_of_birth).format('MM'), 10) - 1 || 0,
          year: parseInt(moment(profile.date_of_birth).format('YYYY'), 10) || 0,
        }, () => {
          this.saveFirstName.resetInitialValue();
          this.saveLastName.resetInitialValue();
          this.saveOrganisation.resetInitialValue();
          this.savePosition.resetInitialValue();
          this.saveDateOfBirth.resetInitialValue();
        });
      })
      .catch(e => {
        this.loading.stop();
        defaultOnErrorHandler(e, 'Error loading personal details');
      });
  }

  handleFirstNameChange(e) {
    const firstName = e.target.value;

    this.setState(state => update(state, {
      profile: {
        firstName: { $set: firstName },
      },
    }), () => this.saveFirstName());
  }

  handleLastNameChange(e) {
    const lastName = e.target.value;

    this.setState(oldState => mutate(oldState, state => {
      state.profile.lastName = lastName;
      // Direct check: Validator keeps *Valid true until validateForm() runs.
      state.validations.lastNameRequiredValid = !!lastName;
    }), () => this.saveLastName());
  }

  handleOrganisationChange(e) {
    const organisation = e.target.value;

    this.setState(state => update(state, {
      profile: {
        organisation: { $set: organisation },
      },
    }), () => this.saveOrganisation());
  }

  handlePositionChange(e) {
    const position = e.target.value;

    this.setState(state => update(state, {
      profile: {
        position: { $set: position },
      },
    }), () => this.savePosition());
  }

  handleDayChange(e) {
    const day = parseInt(e.target.value, 10);

    this.setState({ day }, () => this.saveDateOfBirth());
  }

  handleMonthChange(e) {
    const month = parseInt(e.target.value, 10);

    this.setState({ month }, () => this.saveDateOfBirth());
  }

  handleYearChange(e) {
    const year = parseInt(e.target.value, 10);

    this.setState({ year }, () => this.saveDateOfBirth());
  }

  get dateOfBirthTimeStamp() {
    return moment(_.pick(this.state, 'year', 'month', 'day'));
  }

  updateProfilePicture(files) {
    if (!files.length) return;

    this.profilePictureLoading.start();
    updatePicture(files[0])
      .then(result => this.setState(state => update(state, {
        profile: { profilePictureUrl: { $set: result.data.profile_picture_url } },
      })))
      .catch(e => defaultOnErrorHandler(e, 'Error updating the profile picture'))
      .finally(() => this.profilePictureLoading.stop());
  }

  removeProfilePicture() {
    this.profilePictureLoading.start();
    removePicture()
      .then(result => this.setState(state => update(state, {
        profile: { profilePictureUrl: { $set: _.get(result, 'data.profile_picture_url') } },
      })))
      .catch(e => defaultOnErrorHandler(e, 'Error removing profile picture'))
      .finally(() => this.profilePictureLoading.stop());
  }

  updateEmailFromModalResult(email) {
    this.setState(state => update(state, {
      profile: {
        email: { $set: email },
      },
    }));
  }

  renderDobPicker() {
    const currentYear = (new Date()).getFullYear();
    const daysInMonth = moment({ year: this.state.year, month: this.state.month }).daysInMonth();
    return (
      <div className="date-of-birth-picker">
        <select
          className="date-of-birth-picker-day"
          value={this.state.day}
          onChange={e => this.handleDayChange(e)}
        >
          {_.range(0, daysInMonth).map(d => <option value={d + 1} key={d}>{d + 1}</option>)}
        </select>
        <select
          className="date-of-birth-picker-month"
          value={this.state.month}
          onChange={e => this.handleMonthChange(e)}
        >
          {_.range(0, 12).map(month => (
            <option value={month} key={month}>{moment({ month }).format('MMMM')}</option>
          ))}
        </select>
        <select
          className="date-of-birth-picker-year"
          value={this.state.year}
          onChange={e => this.handleYearChange(e)}
        >
          {_.range(currentYear - 100, currentYear + 1).map(year => (
            <option value={year} key={year}>{year}</option>
          ))}
        </select>
      </div>
    );
  }

  render() {
    return (
      <ErrorBoundary>
        <HeaderWrapper
          ref={c => (this.header = c)}
          title="Personal Details"
          profileCompleteness={this.props.profileCompleteness}
          newNavEnabled={this.props.newNavEnabled}
        />
        <form className="profiles-me-personal-details --user-facing" action="#">
          <Loading ref={c => (this.loading = c)}>
            <section className="form-group profile-picture-form-group">
              {/* eslint-disable jsx-a11y/label-has-associated-control */}
              <label>Profile Picture</label>
              {/* eslint-enable jsx-a11y/label-has-associated-control */}
              <Loading ref={c => (this.profilePictureLoading = c)}>
                <Dropzone
                  onDrop={files => this.updateProfilePicture(files)}
                  multiple={false}
                  accept="image/*"
                >
                  {({ getRootProps, getInputProps, open }) => (
                    <React.Fragment>
                      <div {...getRootProps({ className: 'profile-picture-overlay-container' })}>
                        <img src={this.state.profile.profilePictureUrl} alt="profile" />
                        <input {...getInputProps()} />
                        <div className="profile-picture-overlay">
                          <p>Drag and drop an image here or click to select a file to upload</p>
                        </div>
                      </div>
                      <div className="form-group-info">
                        <p>For best results use .jpg or .png files</p>
                        <div className="form-group-buttons">
                          <p>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => open()}
                            >
                              Change photo
                            </button>
                          </p>
                          <p>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => this.removeProfilePicture()}
                            >
                              Remove photo
                            </button>
                          </p>
                        </div>
                      </div>
                    </React.Fragment>
                  )}
                </Dropzone>
              </Loading>
            </section>
            <section className="form-group">
              <label htmlFor="first-name">
                First Name
                <input
                  id="first-name"
                  placeholder="First Name"
                  value={this.state.profile.firstName}
                  onChange={e => this.handleFirstNameChange(e)}
                  onBlur={() => this.saveFirstName()}
                />
              </label>
              <p className="sip-controls">
                <Loading variant="sip" ref={c => (this.firstNameLoading = c)} />
              </p>
            </section>
            <section className={`form-group ${this.state.validations.lastNameRequiredValid ? '' : 'has-error'}`}>
              <label htmlFor="last-name">
                Last Name
                <input
                  id="last-name"
                  placeholder="Last Name"
                  value={this.state.profile.lastName}
                  onChange={e => this.handleLastNameChange(e)}
                  onBlur={() => this.saveLastName()}
                />
              </label>
              {this.state.validations.lastNameRequiredValid || (
                <span className="help-block">Last name is required</span>
              )}
              <p className="sip-controls">
                <Loading variant="sip" ref={c => (this.lastNameLoading = c)} />
              </p>
            </section>
            <section className="form-group">
              <label htmlFor="company">
                Company
                <input
                  id="company"
                  placeholder="Company"
                  value={this.state.profile.organisation}
                  onChange={e => this.handleOrganisationChange(e)}
                  onBlur={() => this.saveOrganisation()}
                />
              </label>
              <p className="sip-controls">
                <Loading variant="sip" ref={c => (this.organisationLoading = c)} />
              </p>
            </section>
            <section className="form-group">
              <label htmlFor="job-title">
                Job Title
                <input
                  id="job-title"
                  placeholder="Job Title"
                  value={this.state.profile.position}
                  onChange={e => this.handlePositionChange(e)}
                  onBlur={() => this.savePosition()}
                />
              </label>
              <p className="sip-controls">
                <Loading variant="sip" ref={c => (this.positionLoading = c)} />
              </p>
            </section>
            <section className="form-group">
              {/* eslint-disable jsx-a11y/label-has-associated-control */}
              <label className={`${_.includes(this.state.incompleteDetails, 'date_of_birth') ? '--has-incomplete-details' : ''}`}>
                Date of birth
                <span className="incomplete-details-text">
                  Please enter your date of birth to complete your profile.
                </span>
              </label>
              {/* eslint-enable jsx-a11y/label-has-associated-control */}
              {this.renderDobPicker()}
              <p className="sip-controls">
                <Loading variant="sip" ref={c => (this.dateOfBirthLoading = c)} />
              </p>
            </section>
            <section className="form-group">
              {/* eslint-disable jsx-a11y/label-has-associated-control */}
              <label>Email</label>
              {/* eslint-enable jsx-a11y/label-has-associated-control */}
              <input readOnly value={this.state.profile.email} />
              <p>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={e => this.changeEmailModal.present(e, this.state.profile.email)}
                >
                  Change email address
                </button>
              </p>
            </section>
            <EmailChangeModal
              ref={c => (this.changeEmailModal = c)}
              onEmailSaved={email => this.updateEmailFromModalResult(email)}
            />
            <section className="form-group">
              {/* eslint-disable jsx-a11y/label-has-associated-control */}
              <label>Password</label>
              {/* eslint-enable jsx-a11y/label-has-associated-control */}
              <div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={e => this.passwordChangeModal.present(e)}
                >
                  Change password
                </button>
              </div>
              <PasswordChangeModal ref={c => (this.passwordChangeModal = c)} />
            </section>
          </Loading>
        </form>
      </ErrorBoundary>
    );
  }
}
